import { useEffect } from "react";
import type { ViewStyle } from "react-native";
import Animated, {
	useAnimatedStyle,
	useSharedValue,
	withRepeat,
	withSequence,
	withTiming,
} from "react-native-reanimated";
import { themeRoundnessMultiplier } from "../../constants";
import { useAppTheme } from "../../useAppTheme";

type Props = {
	width?: number | `${number}%`;
	height?: number;
	size?: number;
	variant?: "circle" | "rect";
	borderRadius?: number;
	style?: ViewStyle;
};

export const Skeleton = ({
	width,
	height,
	size,
	variant = "rect",
	borderRadius,
	style,
}: Props) => {
	const theme = useAppTheme();
	const opacity = useSharedValue(0.3);

	const resolvedWidth = size ?? width ?? "100%";
	const resolvedHeight = size ?? height ?? 16;

	const resolvedBorderRadius =
		variant === "circle" && typeof resolvedHeight === "number"
			? resolvedHeight / 2
			: (borderRadius ?? theme.roundness * themeRoundnessMultiplier);

	useEffect(() => {
		opacity.value = withRepeat(
			withSequence(
				withTiming(1, { duration: 800 }),
				withTiming(0.3, { duration: 800 }),
			),
			-1,
		);
	}, [opacity]);

	const animatedStyle = useAnimatedStyle(() => ({
		opacity: opacity.value,
	}));

	return (
		<Animated.View
			accessible={false}
			importantForAccessibility="no-hide-descendants"
			style={[
				{
					width: resolvedWidth,
					height: resolvedHeight,
					borderRadius: resolvedBorderRadius,
					borderCurve: "continuous",
					backgroundColor: theme.colors.surfaceVariant,
				},
				animatedStyle,
				style,
			]}
		/>
	);
};
